import React, { useState, useEffect } from 'react';
import { Table, Button, message, Space, Modal, Form, Input, Popconfirm, Breadcrumb, Upload, Progress, Checkbox, Tag } from 'antd';
import { HomeOutlined, PlusOutlined, UploadOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import api from '../api';
import TeacherForm from '../components/TeacherForm';

const TeacherManagementPage = () => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isResetPasswordModalVisible, setIsResetPasswordModalVisible] = useState(false);
  const [isBatchEditModalVisible, setIsBatchEditModalVisible] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [batchPassword, setBatchPassword] = useState("");
  const [batchRole, setBatchRole] = useState(null);
  const [batchEditType, setBatchEditType] = useState('role'); // 'role' or 'password'
  const [batchLoading, setBatchLoading] = useState(false);
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();
  const [importProgress, setImportProgress] = useState({ visible: false, percent: 0, status: 'active' });

  const handleBatchImport = async (file) => {
    try {
      setImportProgress({ visible: true, percent: 0, status: 'active' });

      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/users/teachers/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 50) / progressEvent.total);
          setImportProgress({ visible: true, percent, status: 'active' });
        }
      });

      setImportProgress({ visible: true, percent: 80, status: 'active' });
      await new Promise(resolve => setTimeout(resolve, 300));

      if (response.data.errors && response.data.errors.length > 0) {
        setImportProgress({ visible: true, percent: 100, status: 'exception' });
        Modal.warning({
          title: '导入结果反馈',
          content: (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <p>成功导入: {response.data.successCount} / {response.data.totalCount}</p>
              <p>以下条目导入失败:</p>
              <ul>
                {response.data.errors.map((err, idx) => (
                  <li key={idx} style={{ color: '#ff4d4f' }}>{err}</li>
                ))}
              </ul>
            </div>
          ),
          width: 600
        });
      } else {
        setImportProgress({ visible: true, percent: 100, status: 'success' });
        message.success(`批量导入完成: 成功导入 ${response.data.successCount} 条数据`);
      }

      fetchTeachers();

      setTimeout(() => setImportProgress({ visible: false, percent: 0, status: 'active' }), 2000);
    } catch (error) {
      console.error('Batch import error:', error);
      setImportProgress({ visible: true, percent: 100, status: 'exception' });
      const errorMessage = error.response?.data?.error || '批量导入失败，请检查文件格式或网络连接';
      message.error(errorMessage);
      setTimeout(() => setImportProgress({ visible: false, percent: 0, status: 'active' }), 2000);
    }
    return false;
  };

  const fetchTeachers = async () => {
    setLoading(true);
    try {
      // 获取所有用户，然后筛选出教师和管理员
      const response = await api.get('/users');
      if (Array.isArray(response.data)) {
        // 筛选出 CLASS_TEACHER、ADMIN、LOGISTICS_ADMIN、LOGISTICS_TEACHER、TEACHING_ADMIN 角色
        const staffList = response.data.filter(u => 
          u.role === 'CLASS_TEACHER' || 
          u.role === 'ADMIN' || 
          u.role === 'LOGISTICS_ADMIN' || 
          u.role === 'LOGISTICS_TEACHER' ||
          u.role === 'TEACHING_ADMIN'
        );
        setTeachers(staffList);
      } else {
        setTeachers([]);
        console.error("Expected array from /users but got:", response.data);
      }
    } catch (error) {
      message.error('获取教师列表失败');
      setTeachers([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await api.post('/users', values);
      message.success('教师创建成功');
      setIsCreateModalVisible(false);
      fetchTeachers();
      form.resetFields();
    } catch (error) {
      // 显示后端返回的具体错误信息
      const errorMessage = error.response?.data?.message || '创建失败，请重试';
      message.error(errorMessage);
      console.error('创建教师失败:', error.response?.data);
    }
  };

  const showEditModal = (teacher) => {
    setSelectedTeacher(teacher);
    form.setFieldsValue(teacher);
    setIsEditModalVisible(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();
      const { password, ...payload } = values;
      await api.put(`/users/${selectedTeacher.id}`, payload);
      message.success('教师信息更新成功');
      setIsEditModalVisible(false);
      fetchTeachers();
      form.resetFields();
    } catch (error) {
      console.error('更新教师失败:', error);
      
      // 提取错误信息
      let errorMessage = '更新失败，请重试';
      
      if (error.response) {
        const data = error.response.data;
        
        // 尝试多种方式提取错误信息
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data && data.message) {
          errorMessage = data.message;
        } else if (data && data.error) {
          errorMessage = data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(`更新教师失败: ${errorMessage}`);
    }
  };

  const showResetPasswordModal = (teacher) => {
    setSelectedTeacher(teacher);
    setIsResetPasswordModalVisible(true);
    setNewPassword("");
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      message.error("请输入新密码");
      return;
    }
    try {
      await api.post(`/users/${selectedTeacher.id}/reset-password`, { newPassword });
      message.success('密码重置成功');
      setIsResetPasswordModalVisible(false);
    } catch (error) {
      const errorMessage = error.response?.data?.message || '密码重置失败，请重试';
      message.error(errorMessage);
      console.error('重置密码失败:', error.response?.data);
    }
  };

  const handleDeleteTeacher = async (teacherId) => {
    try {
      await api.delete(`/users/${teacherId}`);
      message.success('教师删除成功');
      fetchTeachers();
    } catch (error) {
      console.error('删除教师失败:', error);
      
      // 提取错误信息
      let errorMessage = '删除失败，请重试';
      
      if (error.response) {
        const data = error.response.data;
        
        // 尝试多种方式提取错误信息
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data && data.message) {
          errorMessage = data.message;
        } else if (data && data.error) {
          errorMessage = data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(`删除教师失败: ${errorMessage}`);
    }
  };

  const showBatchEditModal = (type) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要编辑的教师');
      return;
    }
    setBatchEditType(type);
    setBatchPassword("");
    setBatchRole(null);
    batchForm.resetFields();
    setIsBatchEditModalVisible(true);
  };

  const handleBatchUpdate = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要编辑的教师');
      return;
    }

    if (batchEditType === 'password' && !batchPassword) {
      message.warning('请输入新密码');
      return;
    }

    if (batchEditType === 'role' && !batchRole) {
      message.warning('请选择新角色');
      return;
    }

    setBatchLoading(true);
    try {
      const requests = selectedRowKeys.map(id => {
        if (batchEditType === 'password') {
          return api.post(`/users/${id}/reset-password`, { newPassword: batchPassword });
        } else {
          return api.put(`/users/${id}`, { role: batchRole });
        }
      });

      const results = await Promise.allSettled(requests);
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;

      if (failCount > 0) {
        message.warning(`批量更新完成: 成功 ${successCount} 条, 失败 ${failCount} 条`);
      } else {
        message.success(`批量更新成功: 共 ${successCount} 条`);
      }

      setIsBatchEditModalVisible(false);
      setSelectedRowKeys([]);
      setSelectedRows([]);
      fetchTeachers();
    } catch (error) {
      console.error('批量更新失败:', error);
      message.error('批量更新失败，请重试');
    }
    setBatchLoading(false);
  };

  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns = [
    { title: '姓名', dataIndex: 'name', key: 'name', fontWeight: 600 },
    { title: '账号', dataIndex: 'account', key: 'account' },
    {
      title: '权限',
      dataIndex: 'role',
      key: 'role',
      render: (role) => {
        if (role === 'ADMIN') return <Tag color="red">管理员</Tag>;
        if (role === 'CLASS_TEACHER') return <Tag color="blue">普通教师</Tag>;
        if (role === 'TEACHING_ADMIN') return <Tag color="orange">教学管理员</Tag>;
        if (role === 'LOGISTICS_ADMIN') return <Tag color="purple">后勤管理员</Tag>;
        if (role === 'LOGISTICS_TEACHER') return <Tag color="green">后勤生活老师</Tag>;
        return role || '-';
      }
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 90,
      render: (gender, record) => record.role === 'LOGISTICS_TEACHER' ? (gender || '未设置') : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => showEditModal(record)}>编辑</Button>
          <Button type="link" size="small" onClick={() => showResetPasswordModal(record)}>重置密码</Button>
          <Popconfirm title="确认删除该教师吗？" onConfirm={() => handleDeleteTeacher(record.id)} okText="确认" cancelText="取消">
            <Button type="link" danger size="small">删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const hasSelected = selectedRowKeys.length > 0;

  return (
    <div>
      <div className="page-header">
        <Breadcrumb items={[
          { title: <><HomeOutlined /> 首页</> },
          { title: '用户管理' },
          { title: '教师管理' },
        ]} />
        <h2 className="page-title">教师管理</h2>
      </div>

      <div className="table-card">
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateModalVisible(true)}>
                    新建教师
                </Button>
                <Upload
                    beforeUpload={handleBatchImport}
                    showUploadList={false}
                    accept=".xlsx, .xls"
                >
                    <Button icon={<UploadOutlined />}>批量导入</Button>
                </Upload>
                <Button type="text" icon={<DownloadOutlined />} href="/template-teachers.xlsx" download="教师导入模板.xlsx">
                    下载模板
                </Button>
            </Space>
            <Space>
                {hasSelected && (
                  <span style={{ marginRight: 8 }}>
                    已选择 <a style={{ fontWeight: 600 }}>{selectedRowKeys.length}</a> 项
                  </span>
                )}
                <Button
                  icon={<EditOutlined />}
                  onClick={() => showBatchEditModal('role')}
                  disabled={!hasSelected}
                >
                    批量修改角色
                </Button>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => showBatchEditModal('password')}
                  disabled={!hasSelected}
                >
                    批量重置密码
                </Button>
                {hasSelected && (
                  <Button onClick={() => { setSelectedRowKeys([]); setSelectedRows([]); }}>
                    取消选择
                  </Button>
                )}
            </Space>
        </div>

        {importProgress.visible && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fafafa', borderRadius: 8 }}>
            <div style={{ marginBottom: 8, color: '#666' }}>
              {importProgress.status === 'active' && '正在导入...'}
              {importProgress.status === 'success' && '导入完成！'}
              {importProgress.status === 'exception' && '导入完成（部分失败）'}
            </div>
            <Progress
              percent={importProgress.percent}
              status={importProgress.status}
              strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
            />
          </div>
        )}
        <Table 
            columns={columns} 
            dataSource={teachers} 
            loading={loading} 
            rowKey="id" 
            rowSelection={rowSelection}
            pagination={{ 
                defaultPageSize: 10, 
                showTotal: (total) => `共 ${total} 条记录`,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100]
            }}
        />
      </div>
      
      <Modal title="新建教师" open={isCreateModalVisible} onOk={handleCreate} onCancel={() => setIsCreateModalVisible(false)}>
        <TeacherForm form={form} />
      </Modal>

      <Modal title="编辑教师" open={isEditModalVisible} onOk={handleUpdate} onCancel={() => setIsEditModalVisible(false)}>
        <TeacherForm form={form} isEditMode={true} />
      </Modal>

      <Modal title={`重置密码 - ${selectedTeacher?.name}`} open={isResetPasswordModalVisible} onOk={handleResetPassword} onCancel={() => setIsResetPasswordModalVisible(false)}>
        <Input.Password
          placeholder="请输入新的临时密码"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
      </Modal>

      <Modal
        title={batchEditType === 'role' ? '批量修改角色' : '批量重置密码'}
        open={isBatchEditModalVisible}
        onOk={handleBatchUpdate}
        onCancel={() => setIsBatchEditModalVisible(false)}
        confirmLoading={batchLoading}
        okText="确认"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ marginBottom: 8 }}>
            即将对 <strong>{selectedRowKeys.length}</strong> 位教师进行批量{batchEditType === 'role' ? '修改角色' : '重置密码'}
          </p>
          <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
            {selectedRows.map((teacher, idx) => (
              <div key={teacher.id} style={{ marginBottom: 4 }}>
                <span>{teacher.name}</span>
                <span style={{ color: '#888', marginLeft: 8 }}>({teacher.account})</span>
              </div>
            ))}
          </div>
        </div>

        <Form form={batchForm} layout="vertical">
          {batchEditType === 'role' ? (
            <Form.Item
              label="新角色"
              required
              rules={[{ required: true, message: '请选择新角色' }]}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Checkbox onChange={() => setBatchRole('CLASS_TEACHER')} checked={batchRole === 'CLASS_TEACHER'}>
                  <Tag color="blue">普通教师</Tag>
                </Checkbox>
                <Checkbox onChange={() => setBatchRole('TEACHING_ADMIN')} checked={batchRole === 'TEACHING_ADMIN'}>
                  <Tag color="orange">教学管理员</Tag>
                </Checkbox>
                <Checkbox onChange={() => setBatchRole('LOGISTICS_ADMIN')} checked={batchRole === 'LOGISTICS_ADMIN'}>
                  <Tag color="purple">后勤管理员</Tag>
                </Checkbox>
                <Checkbox onChange={() => setBatchRole('LOGISTICS_TEACHER')} checked={batchRole === 'LOGISTICS_TEACHER'}>
                  <Tag color="green">后勤生活老师</Tag>
                </Checkbox>
              </Space>
            </Form.Item>
          ) : (
            <Form.Item
              label="新密码"
              required
              rules={[{ required: true, message: '请输入新密码' }]}
            >
              <Input.Password
                placeholder="请输入新的临时密码"
                value={batchPassword}
                onChange={(e) => setBatchPassword(e.target.value)}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default TeacherManagementPage;
